import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  clientPath,
  read,
  command,
  type Relationship,
  type GrantScope,
  type VaultService,
  type Switch,
} from '@/services/vault/vault.http'
import {
  Field,
  ServiceSelect,
  ScopeFields,
  normalizeScope,
  useCommand,
  useStepUp,
  CommandStatus,
  actionLabel,
  TERMS,
  RETENTION,
} from './VaultForms'

export default function RelationshipsPanel({
  client,
  owner,
  accountKey,
  onSaved,
}: {
  client: string
  owner: boolean
  accountKey: unknown[]
  onSaved: () => Promise<void>
}) {
  const relationships = useQuery({
    queryKey: [...accountKey, client, 'relationships'],
    queryFn: () => read<{ relationships: Relationship[] }>(`${clientPath(client)}/relationships`),
    staleTime: 0,
  })
  const switches = useQuery({
    queryKey: [...accountKey, client, 'switches'],
    queryFn: () => read<{ items: Switch[] }>(`${clientPath(client)}/switches`),
    staleTime: 0,
  })
  const [mode, setMode] = useState<'engagement' | 'grant' | 'switch'>('engagement'),
    [firm, setFirm] = useState(''),
    [engagement, setEngagement] = useState(''),
    [source, setSource] = useState(''),
    [service, setService] = useState<VaultService>('income_tax'),
    [scope, setScope] = useState<GrantScope>({
      actions: [],
      services: ['income_tax'],
      categories: [],
      financial_years: [],
    }),
    [code, setCode] = useState(''),
    [ack, setAck] = useState(false)
  const [effective, setEffective] = useState(() => new Date().toISOString().slice(0, 16)),
    [expiry, setExpiry] = useState(''),
    [deadline, setDeadline] = useState(() =>
      new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 16)
    )
  const [additionalTargets, setAdditionalTargets] = useState<
    Array<{ service: VaultService; scope: GrantScope }>
  >([])
  const actions = useCommand(),
    stepUp = useStepUp()
  const rows = relationships.data?.relationships ?? []
  const activeSource = rows.find(row => row.relationship_id === source)
  const fresh = async (action: () => Promise<unknown>) => {
    await stepUp(code)
    await action()
    setCode('')
    setAck(false)
    await onSaved()
  }
  return (
    <div className="space-y-4">
      <section className="card card-elevated p-5 space-y-3">
        <h2 className="font-semibold">Firm relationships and exact grants</h2>
        <p className="text-sm text-text-secondary">{RETENTION}</p>
        {relationships.error && <p role="alert">{relationships.error.message}</p>}
        {rows.length === 0 && !relationships.isPending && (
          <p className="text-text-secondary">
            No relationships. An engagement creates no access until you authorize an explicit grant.
          </p>
        )}
        {rows.map(row => (
          <article
            key={row.relationship_id}
            className="border border-surface-overlay rounded-lg p-4 space-y-3"
          >
            <h3 className="font-medium break-all">
              Firm {row.firm_id} · {row.status} · revision {row.revision}
            </h3>
            {row.engagements.map(item => (
              <div key={item.engagement_id}>
                <p className="text-sm">
                  {item.service} · {item.status}
                </p>
                <p className="text-xs text-text-tertiary break-all">
                  Engagement: {item.engagement_id}
                </p>
                {item.grants.map(grant => (
                  <div key={grant.grant_id} className="mt-2 text-sm text-text-secondary">
                    <p>
                      {grant.status}: {grant.scope.actions.map(actionLabel).join(', ')}
                    </p>
                    <p>
                      Categories: {grant.scope.categories.join(', ')} · Years:{' '}
                      {grant.scope.financial_years.join(', ')}
                    </p>
                    <p>Expires: {grant.expires_at ?? 'No expiry specified'}</p>
                    {owner && grant.status === 'active' && (
                      <button
                        className="btn btn-secondary mt-2"
                        disabled={actions.busy || !code.trim()}
                        onClick={() =>
                          void actions.run(() =>
                            fresh(() =>
                              command(`${clientPath(client)}/grants/${grant.grant_id}:revoke`, {
                                idempotency_key: actions.idempotencyKey({
                                  revoke: grant.grant_id,
                                  revision: grant.revision,
                                }),
                              })
                            )
                          )
                        }
                      >
                        Revoke grant using MFA below
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </article>
        ))}
      </section>
      {owner && (
        <section className="card card-elevated p-5 space-y-4">
          <h2 className="font-semibold">Authorize firm access</h2>
          <label className="text-sm text-text-secondary block">
            Action
            <select
              className="input input-bordered w-full"
              value={mode}
              onChange={event => {
                setMode(event.target.value as typeof mode)
                setAck(false)
              }}
            >
              <option value="engagement">Create engagement (no data access)</option>
              <option value="grant">Create exact access grant</option>
              <option value="switch">Request firm switch</option>
            </select>
          </label>
          <form
            className="space-y-4"
            onSubmit={event => {
              event.preventDefault()
              if (!ack) return
              const scoped = normalizeScope({ ...scope, services: [service] })
              let path: string, payload: Record<string, unknown>
              if (mode === 'engagement') {
                path = 'engagements'
                payload = { firm_id: firm, service, terms_version: TERMS }
              } else if (mode === 'grant') {
                path = 'grants'
                payload = {
                  engagement_id: engagement,
                  scope: scoped,
                  effective_at: `${effective}:00Z`,
                  expires_at: expiry ? `${expiry}:00Z` : null,
                  terms_version: TERMS,
                }
              } else {
                if (!activeSource) return
                path = 'switches'
                payload = {
                  source_relationship_id: activeSource.relationship_id,
                  expected_source_revision: activeSource.revision,
                  target_firm_id: firm,
                  targets: [
                    ...additionalTargets.map(target => ({
                      ...target,
                      grant_expires_at: expiry ? `${expiry}:00Z` : null,
                    })),
                    { service, scope: scoped, grant_expires_at: expiry ? `${expiry}:00Z` : null },
                  ],
                  expires_at: `${deadline}:00Z`,
                  terms_version: TERMS,
                }
              }
              void actions.run(() =>
                fresh(() =>
                  command(`${clientPath(client)}/${path}`, {
                    ...payload,
                    idempotency_key: actions.idempotencyKey({ path, ...payload }),
                  })
                )
              )
            }}
          >
            {mode !== 'grant' && (
              <Field
                label={mode === 'switch' ? 'New firm UUID' : 'Firm UUID'}
                value={firm}
                required
                onChange={event => {
                  setFirm(event.target.value)
                  setAck(false)
                }}
              />
            )}
            {mode === 'grant' && (
              <label className="block text-sm text-text-secondary">
                Active engagement
                <select
                  className="input input-bordered w-full"
                  value={engagement}
                  required
                  onChange={event => {
                    const selected = rows
                      .flatMap(row => row.engagements)
                      .find(item => item.engagement_id === event.target.value)
                    setEngagement(event.target.value)
                    if (selected) setService(selected.service)
                    setAck(false)
                  }}
                >
                  <option value="">Select an engagement</option>
                  {rows
                    .filter(row => row.status === 'active')
                    .flatMap(row =>
                      row.engagements
                        .filter(item => item.status === 'active')
                        .map(item => (
                          <option key={item.engagement_id} value={item.engagement_id}>
                            {row.firm_id} · {item.service}
                          </option>
                        ))
                    )}
                </select>
              </label>
            )}
            {mode === 'switch' && (
              <label className="block text-sm text-text-secondary">
                Former firm relationship
                <select
                  className="input input-bordered w-full"
                  required
                  value={source}
                  onChange={event => {
                    setSource(event.target.value)
                    setAck(false)
                  }}
                >
                  <option value="">Select former firm</option>
                  {rows
                    .filter(row => row.status === 'active')
                    .map(row => (
                      <option key={row.relationship_id} value={row.relationship_id}>
                        {row.firm_id}
                      </option>
                    ))}
                </select>
              </label>
            )}
            {mode !== 'grant' && (
              <ServiceSelect
                value={service}
                onChange={value => {
                  setService(value)
                  setAck(false)
                }}
              />
            )}
            {mode !== 'engagement' && (
              <ScopeFields
                scope={scope}
                onChange={value => {
                  setScope(value)
                  setAck(false)
                }}
              />
            )}
            {mode === 'switch' && (
              <div className="space-y-3">
                {additionalTargets.map(target => (
                  <div
                    key={target.service}
                    className="border border-surface-overlay rounded-lg p-3 text-sm"
                  >
                    <p>
                      {target.service}: {target.scope.actions.map(actionLabel).join(', ')} ·{' '}
                      {target.scope.categories.join(', ')} ·{' '}
                      {target.scope.financial_years.join(', ')}
                    </p>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setAdditionalTargets(previous =>
                          previous.filter(item => item.service !== target.service)
                        )
                        setAck(false)
                      }}
                    >
                      Remove this service scope
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={
                    !scope.actions.length ||
                    additionalTargets.length >= 2 ||
                    additionalTargets.some(target => target.service === service)
                  }
                  onClick={() => {
                    const available = (['income_tax', 'gst', 'finance'] as VaultService[]).find(
                      value =>
                        value !== service &&
                        !additionalTargets.some(target => target.service === value)
                    )
                    if (!available) return
                    setAdditionalTargets(previous => [
                      ...previous,
                      { service, scope: normalizeScope({ ...scope, services: [service] }) },
                    ])
                    setService(available)
                    setScope({
                      actions: [],
                      services: [available],
                      categories: [],
                      financial_years: [],
                    })
                    setAck(false)
                  }}
                >
                  Keep this scope and add another service
                </button>
              </div>
            )}
            {mode === 'grant' && (
              <Field
                label="Grant effective time (UTC)"
                type="datetime-local"
                value={effective}
                required
                onChange={event => {
                  setEffective(event.target.value)
                  setAck(false)
                }}
              />
            )}
            {mode === 'switch' && (
              <Field
                label="Acceptance deadline (UTC; at most 7 days)"
                type="datetime-local"
                value={deadline}
                required
                onChange={event => {
                  setDeadline(event.target.value)
                  setAck(false)
                }}
              />
            )}
            {mode !== 'engagement' && (
              <Field
                label="Grant expiry (UTC; optional, after acceptance deadline for switching)"
                type="datetime-local"
                value={expiry}
                onChange={event => {
                  setExpiry(event.target.value)
                  setAck(false)
                }}
              />
            )}
            <p className="text-sm text-text-secondary">
              Consent terms: {TERMS}. {mode === 'switch' && RETENTION}
            </p>
            <label className="flex gap-2 text-sm text-text-secondary">
              <input
                type="checkbox"
                checked={ack}
                onChange={event => setAck(event.target.checked)}
                required
              />
              I authorize this firm, service and the exact scope shown above.
            </label>
            <Field
              label="Fresh authenticator or backup code (also used for revocations)"
              value={code}
              autoComplete="one-time-code"
              required
              onChange={event => setCode(event.target.value)}
            />
            <button
              className="btn btn-primary"
              disabled={
                actions.busy ||
                !ack ||
                !code.trim() ||
                (mode !== 'engagement' && !scope.actions.length) ||
                (mode === 'switch' && additionalTargets.some(target => target.service === service))
              }
            >
              {mode === 'switch'
                ? 'Request firm switch'
                : mode === 'grant'
                  ? 'Authorize exact grant'
                  : 'Create engagement'}
            </button>
          </form>
          <CommandStatus {...actions} />
        </section>
      )}
      <section className="card card-elevated p-5 space-y-3">
        <h2 className="font-semibold">Switch history</h2>
        {switches.error && <p role="alert">{switches.error.message}</p>}
        {switches.data?.items.map(row => (
          <article
            className="border border-surface-overlay rounded-lg p-4 space-y-2"
            key={row.switch_id}
          >
            <p className="break-all">
              {row.source_firm_id} → {row.target_firm_id}
            </p>
            <p>
              {row.status} · revision {row.revision} · deadline {row.expires_at}
            </p>
            <p className="text-sm text-text-secondary">
              {row.terms_version} · {RETENTION}
            </p>
            {owner && row.status === 'pending_acceptance' && (
              <button
                className="btn btn-secondary"
                disabled={actions.busy || !code.trim()}
                onClick={() =>
                  void actions.run(() =>
                    fresh(() =>
                      command(`${clientPath(client)}/switches/${row.switch_id}:cancel`, {
                        expected_revision: row.revision,
                        idempotency_key: actions.idempotencyKey({
                          cancel: row.switch_id,
                          revision: row.revision,
                        }),
                      })
                    )
                  )
                }
              >
                Cancel using MFA above
              </button>
            )}
          </article>
        ))}
      </section>
    </div>
  )
}
